// "Easily guessable" check — mirror of Portal-BE passwordStrength.js, keep in step.
// Rejects common passwords (with trailing digits / symbols / leet spellings),
// alphabet or digit runs and keyboard walks of 4+, repeated characters or
// patterns, and anything containing the account's own username or name.

import { COMMON_PASSWORDS, KEYBOARD_ROWS } from "./passwordBlocklist";

export type WeakPasswordCode = "common" | "sequence" | "repeated" | "contains_username" | "contains_name";

export interface PasswordContext {
    username?: string | null;
    name?: string | null;
}

const COMMON = new Set(COMMON_PASSWORDS);
const LONG_COMMON = COMMON_PASSWORDS.filter((w) => w.length >= 6);

const LEET: Record<string, string> = { "0": "o", "1": "i", "3": "e", "4": "a", "5": "s", "7": "t", "@": "a", "$": "s" };
export const leetFold = (s: string): string => s.replace(/[013457@$]/g, (c) => LEET[c] ?? c);

// ł/Ł has no combining mark, so NFD leaves it alone — map it by hand.
export const normalise = (s: unknown): string =>
    String(s ?? "").replace(/ł/g, "l").replace(/Ł/g, "L")
        .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

const stripEdgeDigits = (s: string): string => s.replace(/^[0-9]+/, "").replace(/[0-9]+$/, "");

const isCommon = (raw: string): boolean => {
    const base = normalise(raw);
    const folded = normalise(leetFold(raw.toLowerCase()));
    for (const c of new Set([base, folded, stripEdgeDigits(base), stripEdgeDigits(folded)])) {
        if (c && COMMON.has(c)) return true;
    }
    for (const c of [base, folded]) {
        for (const w of LONG_COMMON) {
            if (c.length - w.length <= 4 && c.includes(w)) return true;
        }
    }
    return false;
};

const hasSequence = (raw: string): boolean => {
    const s = normalise(raw);
    let run = 1, dir = 0;
    for (let i = 1; i < s.length; i++) {
        const d = s.charCodeAt(i) - s.charCodeAt(i - 1);
        if (d === 1 || d === -1) {
            if (d === dir) run++; else { dir = d; run = 2; }
            if (run >= 4) return true;
        } else { run = 1; dir = 0; }
    }
    for (const row of KEYBOARD_ROWS) {
        const back = row.split("").reverse().join("");
        for (let i = 0; i + 4 <= row.length; i++) {
            if (s.includes(row.slice(i, i + 4)) || s.includes(back.slice(i, i + 4))) return true;
        }
    }
    return false;
};

const isRepeated = (raw: string): boolean => {
    const s = normalise(raw);
    if (/(.)\1{3,}/.test(s)) return true;
    for (let p = 1; p <= 3; p++) {
        if (s.length >= 2 * p && s.length % p === 0 && s === s.slice(0, p).repeat(s.length / p)) return true;
    }
    return new Set(s).size < 4;
};

const containsUsername = (raw: string, username: string | null | undefined): boolean => {
    const u = normalise(username);
    return u.length >= 3 && normalise(raw).includes(u);
};

const containsName = (raw: string, name: string | null | undefined): boolean => {
    const s = normalise(raw);
    return String(name ?? "").split(/[^\p{L}]+/u).map(normalise).filter((t) => t.length >= 4).some((t) => s.includes(t));
};

/** Null when the password is fine, else the reason code. */
export const weakPasswordCode = (password: string, context: PasswordContext = {}): WeakPasswordCode | null => {
    if (!password) return null;
    if (isCommon(password)) return "common";
    if (hasSequence(password)) return "sequence";
    if (isRepeated(password)) return "repeated";
    if (containsUsername(password, context.username)) return "contains_username";
    if (containsName(password, context.name)) return "contains_name";
    return null;
};

/** i18n key for a weak-password code (server or client). */
export const weakPasswordKey = (code: string): string => `password_weak_${code}`;

export const WEAK_PASSWORD_CODES: readonly string[] = ["common", "sequence", "repeated", "contains_username", "contains_name"];
